export interface EavsEmailValidationResult {
  valid?: boolean;
  formatInvalid?: boolean;
  failure?: { reason?: string };
}

export interface EavsEmailValidator {
  validateEmail(email: string): Promise<EavsEmailValidationResult>;
}

export interface EavsVerdict {
  valid: boolean;
  hasVerdict: boolean;
  reason: string;
}

const NO_VERDICT: EavsVerdict = { valid: true, hasVerdict: false, reason: '' };

let cachedEmail = '';
let cachedVerdict: EavsVerdict | null = null;
let inFlightEmail = '';
let inFlightRequest: Promise<EavsVerdict> | null = null;

export const peekEavsVerdict = (rawEmail: string): EavsVerdict | null => {
  const key = (rawEmail ?? '').trim().toLowerCase();
  if (key && key === cachedEmail && cachedVerdict) {
    return cachedVerdict;
  }
  return null;
};

export const checkEavsSignal = (
  validator: EavsEmailValidator,
  rawEmail: string,
): Promise<EavsVerdict> => {
  const email = (rawEmail ?? '').trim();
  if (!email) {
    return Promise.resolve(NO_VERDICT);
  }
  const key = email.toLowerCase();
  if (key === inFlightEmail && inFlightRequest) {
    return inFlightRequest;
  }
  if (key === cachedEmail && cachedVerdict) {
    return Promise.resolve(cachedVerdict);
  }

  const request = (async (): Promise<EavsVerdict> => {
    try {
      const data = await validator.validateEmail(email);
      if (!data) {
        return NO_VERDICT;
      }
      const valid = !data.formatInvalid && !!data.valid;
      const verdict: EavsVerdict = {
        valid,
        hasVerdict: true,
        reason: valid
          ? ''
          : data.failure?.reason ??
            (data.formatInvalid ? 'FORMAT_INVALID' : 'UNKNOWN'),
      };
      cachedEmail = key;
      cachedVerdict = verdict;
      return verdict;
    } catch {
      return NO_VERDICT;
    } finally {
      if (inFlightEmail === key) {
        inFlightEmail = '';
        inFlightRequest = null;
      }
    }
  })();

  inFlightEmail = key;
  inFlightRequest = request;
  return request;
};
